async function addMarkerImagesToMap(maplibreMap) {
  const imgModules = import.meta.glob('/src/img/map/markers/*.png', {
    eager: false,
    query: '?url',
    import: 'default',
  })

  await Promise.all(
    Object.keys(imgModules).map(async (filePath) => {
      const fileName = filePath.split('/').pop()
      const imgUrl = await imgModules[filePath]()
      const image = await maplibreMap.loadImage(imgUrl)
      maplibreMap.addImage(
        `nazka_${fileName.replace(/\.[^/.]+$/, '')}`,
        image.data,
        { sdf: fileName.startsWith('sdf-') },
      )
    }),
  )
}

async function addSvgMarkerImagesToMap(maplibreMap, markersArray) {
  const svgModules = import.meta.glob('/src/img/map/svg-markers/*.svg', {
    eager: false,
    query: '?url',
    import: 'default',
  })

  await Promise.all(
    markersArray.map(async (item) => {
      const iconFileName = item.icon
      const color = item.color
      const imageName = `nazka_${iconFileName.replace(/\.[^/.]+$/, '')}`

      if (maplibreMap.hasImage(imageName)) {
        return
      }

      const svgPath = `/src/img/map/svg-markers/${iconFileName}.svg`
      const svgModule = svgModules[svgPath]

      if (!svgModule) {
        console.warn(`SVG marker not found: ${svgPath}`)
        return
      }

      const svgUrl = await svgModule()

      const response = await fetch(svgUrl)
      const svgText = await response.text()

      const parser = new DOMParser()
      const svgDoc = parser.parseFromString(svgText, 'image/svg+xml')
      const markerColorElements = svgDoc.querySelectorAll('.marker-color')
      markerColorElements.forEach((element) => {
        element.setAttribute('fill', color)
      })
      const modifiedSvgText = new XMLSerializer().serializeToString(svgDoc)

      const dataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(modifiedSvgText)

      const image = new Image()
      await new Promise((resolve) => {
        image.onload = resolve
        image.src = dataUrl
      })

      maplibreMap.addImage(imageName, image)
    }),
  )
}

function createLabResultPieSvg(positiveRatio) {
  const size = 64
  const center = size / 2
  const radius = 29

  if (positiveRatio <= 0) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${center}" cy="${center}" r="${radius}" fill="#16B232"/></svg>`
  }

  if (positiveRatio >= 1) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${center}" cy="${center}" r="${radius}" fill="#FF0000"/></svg>`
  }

  const angle = positiveRatio * Math.PI * 2
  const endX = center + radius * Math.sin(angle)
  const endY = center - radius * Math.cos(angle)
  const largeArcFlag = positiveRatio > 0.5 ? 1 : 0
  const positivePath = [
    `M ${center} ${center}`,
    `L ${center} ${center - radius}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
    'Z',
  ].join(' ')

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${center}" cy="${center}" r="${radius}" fill="#16B232"/><path d="${positivePath}" fill="#FF0000"/></svg>`
}

async function addLabResultPieImagesToMap(maplibreMap) {
  const bucketCount = 20

  await Promise.all(
    Array.from({ length: bucketCount + 1 }, async (_value, bucket) => {
      const imageName = `nazka_lab-result-pie-${bucket}`
      if (maplibreMap.hasImage(imageName)) return

      const svgText = createLabResultPieSvg(bucket / bucketCount)
      const dataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgText)
      const image = new Image()
      await new Promise((resolve) => {
        image.onload = resolve
        image.src = dataUrl
      })

      maplibreMap.addImage(imageName, image)
    }),
  )
}

function getProjectBounds(project) {
  const [[minX, minY], , [maxX, maxY]] = project.bbox.geometry.coordinates[0]
  const bufferFactor = 0.5

  const latBuffer = (maxY - minY) * bufferFactor
  const lonBuffer = (maxX - minX) * bufferFactor

  return [
    [minX - lonBuffer, minY - latBuffer],
    [maxX + lonBuffer, maxY + latBuffer],
  ]
}

async function addPulsingTrapImage(map, marker) {
  const size = 120
  const arcCount = 3 // Number of circles

  const imgModules = import.meta.glob('/src/img/map/markers/*.png', {
    eager: false,
    query: '?url',
    import: 'default',
  })

  const imgPath = `/src/img/map/markers/${marker}.png`
  const imgModule = imgModules[imgPath]

  if (!imgModule) {
    console.warn(`Marker image not found: ${imgPath}`)
    return
  }

  const trapUrl = await imgModule()
  const centerImage = new Image()
  centerImage.src = trapUrl

  const pulsingDot = {
    width: size,
    height: size,
    data: new Uint8Array(size * size * 4),

    onAdd: function () {
      const canvas = document.createElement('canvas')
      canvas.width = this.width
      canvas.height = this.height
      this.context = canvas.getContext('2d', { willReadFrequently: true })
    },

    render: function () {
      const duration = 3000 // Duration for a full pulse cycle
      const maxRadius = (size / 2) * 0.6
      const context = this.context

      context.clearRect(0, 0, this.width, this.height)

      // Draw multiple pulsing circles with phase offsets
      for (let i = 0; i < arcCount; i++) {
        const phaseOffset = i / arcCount // Phase offset for each circle
        const t = ((performance.now() / duration + phaseOffset) % 1)

        const radius = maxRadius * t
        const opacity = 1 - t

        context.beginPath()
        context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2)
        context.strokeStyle = `rgba(255, 100, 100, ${opacity})`
        context.lineWidth = 10
        context.stroke()
      }

      // Draw the PNG image in the center of the canvas at a smaller size
      const imageSize = 38 // Smaller size to match non-pulsing markers
      const imageX = this.width / 2 - imageSize / 2
      const imageY = this.height / 2 - imageSize / 2
      context.drawImage(centerImage, imageX, imageY, imageSize, imageSize + 4)

      this.data = context.getImageData(0, 0, this.width, this.height).data
      map.triggerRepaint()
      return true
    },
  }

  map.addImage(`nazka_${marker}-pulsing`, pulsingDot, { pixelRatio: 2 })
}

export {
  addLabResultPieImagesToMap,
  addMarkerImagesToMap,
  addSvgMarkerImagesToMap,
  getProjectBounds,
  addPulsingTrapImage,
}
